import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { APP_VERSION } from '../version'
import { useStore } from '../store'
import type { ViewName } from '../store'
import { useResultWidth, RESULT_WIDTH_LABELS, RESULT_WIDTH_CLASSES } from '../hooks/useResultWidth'
import type { ResultWidth } from '../hooks/useResultWidth'
import { UploadZone } from './UploadZone'
import { ReconciliationTable } from './ReconciliationTable'
import { HistoryTable } from './HistoryTable'
import { UserManagement } from './UserManagement'
import { LogViewer } from './LogViewer'
import { AccountSettings } from './AccountSettings'
import { ScenarioPanel } from './ScenarioPanel'
import { ReplaceProfiles } from './ReplaceProfiles'
import { BulkRename } from './BulkRename'
import { PriceLists } from './PriceLists'
import { AdminSettings } from './AdminSettings'
import { AuditLogViewer } from './AuditLogViewer'
import { SaveAsTestCaseModal } from './SaveAsTestCaseModal'
import { ValidatorPage } from './ValidatorPage'
import { TestDataGeneratorPage } from './TestDataGeneratorPage'
import { PartyPresetsPage } from './PartyPresetsPage'

type Props = {
  onNewUpload: () => void
}

function WidthPicker({ resultWidth, setResultWidth }: { resultWidth: ResultWidth; setResultWidth: (w: ResultWidth) => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5" title={t('app_views.width_picker_title')}>
      {(['normal', 'wide', 'full'] as ResultWidth[]).map(w => (
        <button
          key={w}
          onClick={() => setResultWidth(w)}
          title={t('app_views.width_picker_option_title', { label: RESULT_WIDTH_LABELS[w] })}
          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
            resultWidth === w
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {RESULT_WIDTH_LABELS[w]}
        </button>
      ))}
    </div>
  )
}

export function AppViews({ onNewUpload }: Props) {
  const { t } = useTranslation()
  const { activeView, currentSession, error, setError, currentUser } = useStore()
  const { resultWidth, setResultWidth } = useResultWidth()
  const [showSaveAsTestCase, setShowSaveAsTestCase] = useState(false)
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null)

  const effectiveView: ViewName = (activeView === 'result' && !currentSession) ? 'upload' : activeView

  // overflow-auto staat altijd op de volle breedte van <main>, zodat scrollen met het
  // muiswiel ook werkt in de marge links/rechts van gecentreerde (max-w) inhoud — anders
  // krimpt het scrollbare vlak mee met de breedtebeperking hieronder.
  const mainClass = effectiveView === 'logs' ? 'flex flex-col overflow-hidden' : 'overflow-auto'

  const contentWidthClass =
    effectiveView === 'result'    ? RESULT_WIDTH_CLASSES[resultWidth] :
    effectiveView === 'validator' ? RESULT_WIDTH_CLASSES[resultWidth] :
    effectiveView === 'history'   ? '' :
    'max-w-6xl mx-auto'

  return (
    <>
      <main className={`flex-1 px-6 py-8 w-full min-h-0 transition-colors duration-200 ${mainClass}`}>
        {effectiveView === 'logs' && currentUser?.is_admin && (
          <div className="flex flex-col flex-1 min-h-0">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 shrink-0">{t('app_views.logs_heading')}</h2>
            <LogViewer />
          </div>
        )}

        {effectiveView !== 'logs' && (
        <div className={contentWidthClass}>
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-start justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-4 text-red-400 hover:text-red-600">&times;</button>
          </div>
        )}

        {effectiveView === 'upload' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('app_views.upload_heading')}</h2>
            <UploadZone />
          </div>
        )}

        {effectiveView === 'result' && currentSession && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">{t('app_views.result_heading')}</h2>
              <div className="flex items-center gap-3">
                <WidthPicker resultWidth={resultWidth} setResultWidth={setResultWidth} />
                {currentUser?.is_admin && (
                  <button
                    onClick={() => { setSaveSuccessMsg(null); setShowSaveAsTestCase(true) }}
                    className="text-sm text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 rounded-lg px-3 py-1.5 transition-colors"
                    title={t('app_views.save_as_testcase_tooltip')}
                  >
                    {t('app_views.save_as_testcase_button')}
                  </button>
                )}
                <button onClick={onNewUpload} className="text-sm text-blue-600 hover:underline">
                  {t('app_views.new_comparison_button')}
                </button>
              </div>
            </div>
            {saveSuccessMsg && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-2.5 text-sm flex items-center justify-between">
                <span>{saveSuccessMsg}</span>
                <button onClick={() => setSaveSuccessMsg(null)} className="ml-4 text-green-400 hover:text-green-600">&times;</button>
              </div>
            )}
            <ReconciliationTable session={currentSession} />
          </div>
        )}

        {effectiveView === 'history' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('app_views.history_heading')}</h2>
            <HistoryTable />
          </div>
        )}

        {effectiveView === 'scenarios' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('app_views.scenarios_heading')}</h2>
            <ScenarioPanel />
          </div>
        )}

        {effectiveView === 'validator' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900">{t('app_views.validator_heading')}</h2>
              <WidthPicker resultWidth={resultWidth} setResultWidth={setResultWidth} />
            </div>
            <p className="text-sm text-gray-500 mb-6">
              {t('app_views.validator_description')}
            </p>
            <ValidatorPage />
          </div>
        )}

        {effectiveView === 'bulk-rename' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('app_views.bulk_rename_heading')}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {t('app_views.bulk_rename_description')}
            </p>
            <BulkRename />
          </div>
        )}

        {effectiveView === 'replace-profiles' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('app_views.replace_profiles_heading')}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {t('app_views.replace_profiles_description')}
            </p>
            <ReplaceProfiles />
          </div>
        )}

        {effectiveView === 'pricelists' && currentUser?.is_admin && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('app_views.pricelists_heading')}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {t('app_views.pricelists_description')}
            </p>
            <PriceLists />
          </div>
        )}

        {effectiveView === 'users' && currentUser?.is_admin && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('app_views.users_heading')}</h2>
            <UserManagement />
          </div>
        )}

        {effectiveView === 'admin-settings' && currentUser?.is_admin && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('app_views.admin_settings_heading')}</h2>
            <AdminSettings />
          </div>
        )}

        {effectiveView === 'audit-log' && currentUser?.is_admin && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('app_views.audit_log_heading')}</h2>
            <AuditLogViewer />
          </div>
        )}

        {effectiveView === 'testdata-generator' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('app_views.testdata_generator_heading')}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {t('app_views.testdata_generator_description')}
            </p>
            <TestDataGeneratorPage />
          </div>
        )}

        {effectiveView === 'party-presets' && currentUser?.is_admin && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('app_views.party_presets_heading')}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {t('app_views.party_presets_description')}
            </p>
            <PartyPresetsPage />
          </div>
        )}

        {effectiveView === 'account' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('app_views.account_heading')}</h2>
            <AccountSettings />
          </div>
        )}

        <footer className="mt-12 pt-4 border-t border-blue-100/50 dark:border-gray-800 text-center text-xs text-blue-400/70 dark:text-blue-600/70 transition-colors duration-200">
          XML Insights v{APP_VERSION}
        </footer>
        </div>
        )}
      </main>

      {showSaveAsTestCase && currentSession && (
        <SaveAsTestCaseModal
          session={currentSession}
          onClose={() => setShowSaveAsTestCase(false)}
          onSaved={(scenarioName, caseName) => {
            setShowSaveAsTestCase(false)
            setSaveSuccessMsg(t('app_views.save_testcase_success', { caseName, scenarioName }))
          }}
        />
      )}
    </>
  )
}
